import { useState } from "react";
import styled from "@emotion/styled";
import { GrButton, GrDivider, GrMenu, GrStack, palette } from "@jnpmedi/grebob";
import { AuditTrailViewType, SignerAuditTrail } from "@newComponents/common/SignerAuditTrail";
import { FileDetailContainer } from "@newComponents/common/Viewer/FileDetailContainer";
import { DocumentSignerSignatureExt, scrollTo, SigningMethod, StampImageSignature } from "@newComponents/common/Viewer/types";
import { isStampImageSignature } from "@newComponents/common/Viewer/Viewer.function";
import { CommonReferrerViewSection } from "@newComponents/sign/CommonReferrerViewSection";
import { CommonSignerSigneeSection } from "@newComponents/sign/CommonSignerSigneeSection";
import { DocumentSignMergePollingType, langHelper, MvnMessage } from "@newLibs/utils";
import { UseBoolean, useBoolean } from "@newHooks/common/useBoolean";
import { useInterval } from "@newHooks/common/useInterval";
import { useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { useDocumentScrollContext } from "@newHooks/context/document/documentScroll.context";
import { notify } from "@newHooks/context/snackbar/notify";
import { FileDetailViewerContextProvider } from "@newHooks/context/viewer/fileDetailViewer.context";
import { useDownloadCertificateBySignerTokenMutation } from "@newHooks/mutation/signers/downloadCertificateBySignerToken.mutation";
import { useDownloadDocumentWithCertificateBySignerTokenMutation } from "@newHooks/mutation/signers/downloadDocumentSignWithCertificateBySignerToken.mutation";
import { useMergeDocumentSignWithCertificateBySignerTokenMutation } from "@newHooks/mutation/signers/mergeDocumentSignWithCertificateBySignerToken.mutation";
import { useDeclineDocumentWithSignerTokenMutation } from "@newHooks/mutation/signs/declineDocumentWithSignerToken.mutation";
import { useSignDocumentWithSignerTokenMutation } from "@newHooks/mutation/signs/signDocumentWithSignerToken.mutation";
import { useMergedDocumentDownloadStatusQuery } from "@newHooks/query/documentSign/mergedDocumentDownloadStatus.query";
import { usePapyrusServiceInspectQuery } from "@newHooks/query/serviceInspect/papyrusServiceInspect.query";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";
import { SignAgreementDialog } from "./dialogs/SignAgreementDialog";
import { SignaturePadDialog } from "./dialogs/SignaturePadDialog";
import { SignDeclineDialog } from "./dialogs/SignDeclineDialog";
import { CommonSignRequestReasonLeftSideBar } from "../../newComponents/sign/CommonSignRequestReason";
import { DOCS_DOCUMENT_CODE } from "@maven-msg/code/docs";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DOCS_COMMON_LANG } from "@maven-msg/lang/docs/common";
import { LoLang } from "@maven-rest/common/common.types";
import {
  DocumentSigner,
  DocumentSignerAttr,
  DocumentSignerAttrDateValueFormat,
  DocumentSignerAttrTarget,
  DocumentSignerAttrType,
  DocumentSignerSignature,
  DocumentSignerStatus,
  DocumentSignResource,
  DocumentSignStatus,
  DocumentSignType,
  SignDocumentWithSignerTokenRequest,
} from "@maven-rest/docs/DocumentSign.dto";
import { DocumentAccessRequesterType } from "@maven-rest/docs/DocumentSigner.dto";
import { FileExportHistoryStatus } from "@maven-rest/docs/FileExportHistory.dto";
import { compareBigintSafe } from "@maven-helper/functions/Misc.function";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";

interface CommonSignerViewProps {
  token: string;
  documentSign: DocumentSignResource;
  documentSigner: DocumentSigner;
  signatureFields: Array<DocumentSignerAttr>;
  isAgentAvailable: UseBoolean;
  isDownloadFilesCreating: boolean;
  onRefreshDocument: () => void;
}

const DOCUMENT_SIGN_FINALIZED_QS_KEY = "document_sign_finalized";
const DOCUMENT_SIGN_DENIED_QS_KEY = "document_sign_denied";
const DEFAULT_DOCUMENT_SIGN_MERGE_POLLING: DocumentSignMergePollingType = { fileExportHistoryId: BigInt(-1), isPause: true };

export function CommonSignerView({
  token,
  documentSign,
  documentSigner,
  signatureFields,
  isAgentAvailable,
  isDownloadFilesCreating,
  onRefreshDocument,
}: CommonSignerViewProps) {
  const loLang = langHelper.getLang();

  const { markers } = useDocumentMarkerContext();
  const { setPageScroll } = useDocumentScrollContext();

  const [signatures, setSignatures] = useState<DocumentSignerSignatureExt[]>([]);
  const [signingSignatureAttrUuid, setSigningSignatureAttrUuid] = useState<string>("");
  const [signingReason, setSigningReason] = useState<string>("");
  const [signingReasonAttrUuid, setSigningReasonAttrUuid] = useState<string>("");

  const isSignaturePadDialogOpen = useBoolean(false);
  const isDeclineDialogOpen = useBoolean(false);
  const isSignatureAgreementDialogOpen = useBoolean(false);
  const [signDocumentDownloadEl, setSignDocumentDownloadEl] = useState<HTMLElement | null>(null);

  const [documentSignMergeDownloadPollingStatus, setDocumentSignMergeDownloadPollingStatus] = useState<DocumentSignMergePollingType>({
    ...DEFAULT_DOCUMENT_SIGN_MERGE_POLLING,
  });

  usePapyrusServiceInspectQuery();

  const handleMarkerClicked = (signerUuid: string, attrUuid: string, attr: DocumentSignerAttrType) => {
    if (attr === DocumentSignerAttrType.Signature || attr === DocumentSignerAttrType.AgentSignature) {
      setSigningSignatureAttrUuid(attrUuid);
      isSignaturePadDialogOpen.onTrue();
    } else if (attr === DocumentSignerAttrType.AgentAvailableMark) {
      setSignatures([]);
      isAgentAvailable.onToggle();
    }
  };

  const scrollToSignatureField = () => {
    const candidate = signatureFields
      .filter((e) => !signatures.find((s) => s.attrUuid === e.attrUuid))
      .sort((a, b) => (a.coordY || 0) - (b.coordY || 0))?.[0];

    // NOTE: candidate pageIndex는 0 부터 pageScroll은 1부터 시작
    setPageScroll((candidate?.pageIndex || 0) + 1);

    notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_ENTER_NOTI), "WARN");
  };

  const getRequestPayload = (dryrun: boolean): SignDocumentWithSignerTokenRequest => {
    const stampSignatureFilter: StampImageSignature | undefined = signatures.filter((sign) => isStampImageSignature(sign.signature))[0]
      ?.signature as StampImageSignature;

    // NOTE: 손으로 그린 서명만 signatures Payload에 base64로 전달
    const excludedStampBase64: DocumentSignerSignature[] = signatures.map((sign) => ({
      ...sign,
      signature: isStampImageSignature(sign.signature) ? { key: sign.signature.key, bucket: sign.signature.bucket } : sign.signature,
    }));

    const defaultSignerAttrs = [
      {
        target: DocumentSignerAttrTarget.Signer,
        attr: DocumentSignerAttrType.Date,
        value: dayjs().format(DocumentSignerAttrDateValueFormat),
        groupUuid: uuidv4(),
        attrUuid: uuidv4(),
        pageIndex: null,
        coordX: null,
        coordY: null,
      },
      {
        target: DocumentSignerAttrTarget.Signer,
        attr: DocumentSignerAttrType.SigningReason,
        value: signingReason || undefined,
        groupUuid: uuidv4(),
        attrUuid: uuidv4(),
        pageIndex: null,
        coordX: null,
        coordY: null,
      },
    ];

    if (stampSignatureFilter) {
      const { stampBase64, ...rest } = stampSignatureFilter;

      // NOTE: 추후 전자서명 이미지를 저장하는 기능 구현을 대비해 추가적인 Attribute전달
      defaultSignerAttrs.push({
        target: DocumentSignerAttrTarget.Signer,
        attr: DocumentSignerAttrType.SignatureImage,
        value: JSON.stringify(rest),
        groupUuid: uuidv4(),
        attrUuid: uuidv4(),
        pageIndex: null,
        coordX: null,
        coordY: null,
      });
    }

    return {
      token,
      accessToken: documentSign.accessToken,
      // TODO: V1서명 당시에 쓰이던 속성으로 보이고, rest spec에는 required 값임. 스펙 변경 필요.
      signature: "",
      signatures: excludedStampBase64,
      attrs: markers
        .filter((e) =>
          [
            DocumentSignerAttrType.ResponsiveItemValue,
            DocumentSignerAttrType.AgentResponsiveItemValue,
            DocumentSignerAttrType.InputItemValue,
            DocumentSignerAttrType.AgentInputItemValue,
            DocumentSignerAttrType.AgentAvailableMark,
            DocumentSignerAttrType.DropdownItemValue,
            DocumentSignerAttrType.AgentDropdownItemValue,
            DocumentSignerAttrType.DateItemValue,
            DocumentSignerAttrType.AgentDateItemValue,
            DocumentSignerAttrType.AddressItemValue,
            DocumentSignerAttrType.AgentAddressItemValue,
            DocumentSignerAttrType.FollowWritingItemValue,
            DocumentSignerAttrType.AgentFollowWritingItemValue,
          ].includes(e.attr)
        )
        .map<DocumentSignerAttr>((attr) => ({
          target: attr.target,
          attr: attr.attr,
          value: attr.value || "",
          groupUuid: attr.groupUuid,
          attrUuid: attr.signerUuid,
          pageIndex: attr.page,
          coordX: attr.x,
          coordY: attr.y,
        }))
        .concat(defaultSignerAttrs),
      dryrun,
    };
  };

  const { mutate: signRequest, isLoading: isSignLoading } = useSignDocumentWithSignerTokenMutation({
    onSuccess: (data) => {
      isSignatureAgreementDialogOpen.onFalse();

      window.document.location.href = `${document.location.pathname}?access_token=${encodeURIComponent(
        data.documentSign.accessToken
      )}&${DOCUMENT_SIGN_FINALIZED_QS_KEY}=true`;
    },
    onError: (error) => {
      const errRespCode = error?.responseCode;
      if (errRespCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_STATUS_CANCELED.code) {
        onRefreshDocument();
      }
      const dryrun = errRespCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_DRYRUN_SUCC.code;

      if (dryrun) {
        isSignatureAgreementDialogOpen.onTrue();
        return "";
      }

      const missing = errRespCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_RESP_FIELD_MISSING.code;
      const invalid = errRespCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_RESP_FIELD_INVALID.code;

      if (missing || invalid) {
        const message =
          (invalid
            ? DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_RESP_FIELD_INVALID[loLang === LoLang.KO ? "ko" : "en"]
            : DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_RESP_FIELD_MISSING[loLang === LoLang.KO ? "ko" : "en"]) ||
          error.responseMessage ||
          "Unknown error has occurred.";
        const markerId = error?.response?.id;
        const pageIndex = error?.response?.page;
        const element = document.getElementById(`mdx--pdf-marker--id-${markerId}`);
        const page = document.getElementById(`mdx--pdf--page-${pageIndex}`);

        if (element) scrollTo(element, window.innerHeight / -4);
        else if (page) scrollTo(page, window.innerHeight / -2);

        notify(message, "WARN");
      }

      // NOTE: 이미 서명이 완료된 문서에 서명하려는 경우, 에러 메세지 출력 후 페이지 리로드
      if (errRespCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_STATUS_SIGNED.code) {
        onRefreshDocument();
      }

      isSignatureAgreementDialogOpen.onFalse();
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_FAIL, { message: error.responseMessage }), "WARN");
    },
  });

  const { mutate: declineDocumentWithSignerTokenRequest } = useDeclineDocumentWithSignerTokenMutation({
    onSuccess: (resp) => {
      window.document.location.href = `${document.location.pathname}?access_token=${encodeURIComponent(
        resp.documentSign.accessToken
      )}&${DOCUMENT_SIGN_DENIED_QS_KEY}=true`;
    },
    onError: (error) => {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_DECLINE_FAIL, { message: error.responseMessage }), "WARN");
    },
  });

  const { mutate: downloadCertificateBySignerTokenRequest, isLoading: isDownloadCertificateBySignerTokenLoading } =
    useDownloadCertificateBySignerTokenMutation({
      pathVariable: { token },
    });

  const {
    mutate: downloadDocumentSignWithCertificateBySignerTokenRequest,
    isLoading: isDownloadDocumentSignWithCertificateBySignerTokenLoading,
  } = useDownloadDocumentWithCertificateBySignerTokenMutation({
    pathVariable: { fileExportHistoryId: documentSignMergeDownloadPollingStatus.fileExportHistoryId, signerToken: token },
  });

  const { mutate: mergeDocumentSignWithCertificateBySignerTokenRequest, isLoading: mergeDocumentSighWithCertificateBySignerTokenLoading } =
    useMergeDocumentSignWithCertificateBySignerTokenMutation(
      {
        signerToken: token,
        documentSignAccessToken: documentSign.accessToken,
        documentAccessRequesterType: DocumentAccessRequesterType.Signer,
      },
      {
        onSuccess: (data) => {
          // NOTE: 여기에다가 폴링트리거 할 것.
          if (data.fileExportHistoryId) {
            setDocumentSignMergeDownloadPollingStatus({ fileExportHistoryId: data.fileExportHistoryId, isPause: false });
          }
        },
        onError: (error) => {
          notify(MvnMessage(COMMON_LANG.ERROR, { message: error.responseMessage }), "WARN");
        },
      }
    );

  const { refetch, isInitialLoading: getMergeDocumentDownloadStatusLoading } = useMergedDocumentDownloadStatusQuery(
    { fileExportHistoryId: documentSignMergeDownloadPollingStatus.fileExportHistoryId },
    {
      onSuccess: (data) => {
        const mergeStatus = data.status;
        if ([FileExportHistoryStatus.Done, FileExportHistoryStatus.Failed].includes(mergeStatus)) {
          setDocumentSignMergeDownloadPollingStatus({ ...documentSignMergeDownloadPollingStatus, isPause: true });

          if (mergeStatus === FileExportHistoryStatus.Done) {
            downloadDocumentSignWithCertificateBySignerTokenRequest({
              fileExportHistoryId: documentSignMergeDownloadPollingStatus.fileExportHistoryId,
              signerToken: token,
              documentSignAccessToken: documentSign.accessToken,
              documentAccessRequesterType: DocumentAccessRequesterType.Signer,
            });
          }

          if (mergeStatus === FileExportHistoryStatus.Failed) {
            notify("Error Occurred", "WARN");
          }
        }
      },
      onError: (error) => {
        setDocumentSignMergeDownloadPollingStatus({ ...documentSignMergeDownloadPollingStatus, isPause: true });
        notify(MvnMessage(COMMON_LANG.ERROR, { message: error.responseMessage }), "WARN");
      },
      enabled: false,
    }
  );

  useInterval(
    () => {
      refetch();
    },
    3000,
    resolvePollingPause(documentSignMergeDownloadPollingStatus)
  );

  const handleDocumentSignDryrun = (newSignatures?: DocumentSignerSignatureExt[]) => {
    const dryrunPayload = getRequestPayload(true);
    signRequest({ ...dryrunPayload, signatures: newSignatures || dryrunPayload.signatures });
  };

  const handleDownloadSignDocument = () => {
    if (!documentSign || !documentSign?.documentUrl) {
      return;
    }

    return (window.location.href = documentSign.documentUrl);
  };

  const handleDownloadCertificate = () => {
    downloadCertificateBySignerTokenRequest({
      token,
      accessToken: documentSign.accessToken,
    });
  };

  const handleDownloadSignDocumentWithCertificate = () => {
    mergeDocumentSignWithCertificateBySignerTokenRequest({
      signerToken: token,
      documentSignAccessToken: documentSign.accessToken,
      documentAccessRequesterType: DocumentAccessRequesterType.Signer,
    });
  };

  const handleSignatureDialogClose = (newSignatures?: DocumentSignerSignatureExt[]) => {
    isSignaturePadDialogOpen.onFalse();

    if (!!newSignatures && Array.isArray(newSignatures)) {
      if (newSignatures.length === signatureFields.length) {
        handleDocumentSignDryrun(newSignatures);
      } else {
        const nextCandidates = signatureFields
          .filter((e) => !newSignatures.find((s) => s.attrUuid === e.attrUuid))
          .sort((a, b) => (a.coordY || 0) - (b.coordY || 0))?.[0];

        setPageScroll((nextCandidates?.pageIndex || 0) + 1);
      }
    }
  };

  const handleSignAgreementDialogConfirm = () => {
    signRequest({ ...getRequestPayload(false) });
  };

  const signingMethod: SigningMethod.Each | SigningMethod.AtOnce | false = isSignable(documentSigner, documentSign)
    ? documentSigner.attrs.some((e) => [DocumentSignerAttrType.Signature, DocumentSignerAttrType.AgentSignature].includes(e.attr))
      ? SigningMethod.Each
      : documentSigner.attrs.some((e) => e.attr === DocumentSignerAttrType.Sign)
      ? SigningMethod.AtOnce
      : false
    : false;

  const inProgressSignButtonGroup = (
    <GrStack gap={12}>
      <GrButton variant="outlined" color="grey" onClick={() => isDeclineDialogOpen.onTrue()}>
        {MvnMessage(
          documentSign.type === DocumentSignType.EConsent
            ? DOCS_DOCUMENT_LANG.DOCUMENT_ECONSENT_DECLINE_BUTTON
            : DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_DECLINE_BUTTON
        )}
      </GrButton>

      {signingMethod === SigningMethod.Each && signatures.length !== signatureFields.length && (
        <GrButton onClick={scrollToSignatureField}>
          {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ECONSENT_FILED_TO_FILL_BUTTON, {
            count: signatures.length,
            total: signatureFields.length,
          })}
        </GrButton>
      )}
      {signingMethod === SigningMethod.Each && signatures.length === signatureFields.length && (
        <GrButton onClick={() => handleDocumentSignDryrun()} isLoading={isSignLoading}>
          {resolveSignatureTypeButtonName(documentSign?.type) === "e-Consent"
            ? MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ECONSENT_COMPLETE_BUTTON, { count: signatures.length, total: signatureFields.length })
            : MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_COMPLETE_BUTTON, { count: signatures.length, total: signatureFields.length })}
        </GrButton>
      )}
    </GrStack>
  );

  const finalizedSignButtonGroup = (
    <GrStack>
      <GrButton
        onClick={(event) => setSignDocumentDownloadEl(event.currentTarget)}
        startIcon="arrow-bar-to-down"
        endIcon={!!signDocumentDownloadEl ? "chevron-up" : "chevron-down"}
        isLoading={
          isDownloadDocumentSignWithCertificateBySignerTokenLoading ||
          isDownloadFilesCreating ||
          mergeDocumentSighWithCertificateBySignerTokenLoading ||
          isDownloadCertificateBySignerTokenLoading ||
          getMergeDocumentDownloadStatusLoading ||
          !documentSignMergeDownloadPollingStatus.isPause
        }
      >
        {MvnMessage(DOCS_COMMON_LANG.DOWNLOAD_NOUN)}
      </GrButton>
      <GrMenu
        anchorElement={signDocumentDownloadEl}
        items={[
          {
            type: "item",
            disabled: !isDownloadable(documentSign.status),
            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_DOWNLOAD_BUTTON),
            onClick: handleDownloadSignDocument,
          },
          {
            type: "item",
            disabled: !documentSign?.documentUrl || !isDownloadable(documentSign.status),
            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_DOWNLOAD_REPORT_BUTTON),
            onClick: handleDownloadCertificate,
          },
          {
            type: "item",
            disabled: !documentSign?.documentUrl || !isDownloadable(documentSign.status),
            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_DOWNLOAD_MERGED_BUTTON),
            onClick: handleDownloadSignDocumentWithCertificate,
          },
        ]}
        position={{ horizontal: "right", vertical: "bottom" }}
        onClose={() => setSignDocumentDownloadEl(null)}
      />
    </GrStack>
  );

  const notVisibleButtonGroup = <GrStack></GrStack>;

  const rightButtonGroup = signingMethod
    ? inProgressSignButtonGroup
    : isDownloadableCandidate(documentSign.status)
    ? finalizedSignButtonGroup
    : notVisibleButtonGroup;

  return (
    <FileDetailViewerContextProvider>
      <FileDetailContainer
        header={{
          left: notVisibleButtonGroup,
          title: documentSign.title,
          right: rightButtonGroup,
        }}
        leftSidebar={{
          children: (
            <LeftSidebarWrapper>
              <CommonSignerSigneeSection documentSignResource={documentSign} />
              <GrDivider spacing={12} />
              <CommonReferrerViewSection
                containerStyle={{ padding: "0 16px 0", width: "100%" }}
                referrers={documentSign.referrers}
                viewType={AuditTrailViewType.LeftSide}
              />

              {!!documentSign.referrers.length && <GrDivider spacing={12} />}
              {documentSign.requestReason && <CommonSignRequestReasonLeftSideBar requestReason={documentSign.requestReason} />}
              <SignerAuditTrail documentSign={documentSign} viewType={AuditTrailViewType.LeftSide} />
            </LeftSidebarWrapper>
          ),
        }}
        onMarkerClicked={handleMarkerClicked}
        signatures={signatures}
      />
      {isDeclineDialogOpen.value && (
        <SignDeclineDialog
          open={isDeclineDialogOpen}
          documentSignType={documentSign.type}
          onConfirm={() =>
            declineDocumentWithSignerTokenRequest({
              token,
              accessToken: documentSign.accessToken,
            })
          }
        />
      )}
      {isSignatureAgreementDialogOpen.value && (
        <SignAgreementDialog
          open={isSignatureAgreementDialogOpen}
          documentSignType={documentSign.type}
          onConfirm={handleSignAgreementDialogConfirm}
          isLoading={isSignLoading}
        />
      )}
      {isSignaturePadDialogOpen.value && (
        <SignaturePadDialog
          open={isSignaturePadDialogOpen}
          documentSign={documentSign}
          signatures={signatures}
          setSignatures={setSignatures}
          signingReason={signingReason}
          signingReasonAttrUuid={signingReasonAttrUuid}
          setSigningReason={setSigningReason}
          setSigningReasonAttrUuid={setSigningReasonAttrUuid}
          signingSignatureAttrUuid={signingSignatureAttrUuid}
          signerEmail={documentSigner.email}
          signerIndex={documentSigner.signerIndex}
          token={token}
          accessToken={documentSign.accessToken}
          onDialogClose={handleSignatureDialogClose}
        />
      )}
    </FileDetailViewerContextProvider>
  );
}

function resolvePollingPause(req: DocumentSignMergePollingType): boolean {
  return compareBigintSafe(req.fileExportHistoryId, BigInt(-1)) || req.isPause;
}

function resolveSignatureTypeButtonName(documentSignType: DocumentSignType) {
  return documentSignType === DocumentSignType.EConsent ? "e-Consent" : "E-Sign";
}

function isSignable(documentSigner: DocumentSigner, documentSign: DocumentSignResource): boolean {
  return (
    documentSigner.isMine &&
    (documentSigner.status === DocumentSignerStatus.Delivered ||
      documentSigner.status === DocumentSignerStatus.DeliveringSkipped ||
      documentSigner.status === DocumentSignerStatus.DeliveringFailed ||
      documentSigner.status === DocumentSignerStatus.Opened) &&
    [
      DocumentSignStatus.Wait,
      DocumentSignStatus.Delivering,
      DocumentSignStatus.Delivered,
      DocumentSignStatus.SignerDeliveringFailed,
      DocumentSignStatus.Signing,
    ].includes(documentSign.status)
  );
}

function isDownloadableCandidate(documentSignStatus: DocumentSignStatus): boolean {
  return [DocumentSignStatus.Signed, DocumentSignStatus.Finalizing, DocumentSignStatus.Finalized].includes(documentSignStatus);
}

function isDownloadable(documentSignStatus: DocumentSignStatus): boolean {
  return [DocumentSignStatus.Finalized].includes(documentSignStatus);
}

const LeftSidebarWrapper = styled("aside")`
  display: flex;
  flex-direction: column;

  position: sticky;
  top: calc(${baseHeight.appHeaderHeight} + ${baseHeight.appFileDetailHeaderHeight});

  background-color: ${palette["white"]};

  width: ${baseWidth.appSidebarAuditTrailDrawer};
  height: calc(100vh - ${baseHeight.appHeaderHeight} - ${baseHeight.appFileDetailHeaderHeight});

  overflow-y: auto;

  @media (max-width: 768px) {
    width: 100%;
  }
`;
